import { useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import EmptyState from '@/components/base/EmptyState';
import DramaCard from '@/components/feature/DramaCard';
import { dramas } from '@/mocks/dramas';
import { newsList } from '@/mocks/news';
import { usePageMeta } from '@/hooks/usePageMeta';

interface SearchResult {
  key: string;
  type: 'tv' | 'news';
  id: string;
  title: string;
  excerpt: string;
  image: string;
  meta: string;
  tags: string[];
}

const hotKeywords = ['我妻子的一切', '悬疑', '治愈', '花絮', '都市情感', '专访'];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get('q')?.trim() ?? '';
  const [input, setInput] = useState(keyword);

  usePageMeta(
    keyword ? `搜索“${keyword}” - 我妻子的一切影视资讯` : '搜索 - 我妻子的一切影视资讯',
    keyword
      ? `在「我妻子的一切」影视资讯站中搜索“${keyword}”的结果，涵盖相关电视剧与花絮资讯。`
      : '输入关键词，搜索本站收录的电视剧剧集与花絮资讯。',
    '搜索,我妻子的一切,电视剧搜索,花絮搜索,影视资讯',
  );

  const results = useMemo<SearchResult[]>(() => {
    if (!keyword) return [];
    const q = keyword.toLowerCase();

    const dramaResults: SearchResult[] = dramas
      .filter((drama) =>
        [drama.title, drama.synopsis, drama.director, ...drama.genres, ...drama.cast]
          .join(' ')
          .toLowerCase()
          .includes(q),
      )
      .map((drama) => ({
        key: `tv-${drama.id}`,
        type: 'tv',
        id: drama.id,
        title: drama.title,
        excerpt: drama.synopsis,
        image: drama.cover,
        meta: `${drama.status} · 全 ${drama.episodes} 集 · 更新于 ${drama.updateDate}`,
        tags: drama.genres,
      }));

    const newsResults: SearchResult[] = newsList
      .filter((news) =>
        [news.title, news.excerpt, news.tags.join(' '), news.body.join(' ')]
          .join(' ')
          .toLowerCase()
          .includes(q),
      )
      .map((news) => ({
        key: `news-${news.id}`,
        type: 'news',
        id: news.id,
        title: news.title,
        excerpt: news.excerpt,
        image: news.cover,
        meta: `花絮资讯 · ${news.date}`,
        tags: news.tags,
      }));

    return [...dramaResults, ...newsResults];
  }, [keyword]);

  const recommend = useMemo(() => [...dramas].sort((a, b) => b.hot - a.hot).slice(0, 4), []);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = input.trim();
    setSearchParams(next ? { q: next } : {});
  };

  const linkFor = (item: SearchResult) =>
    item.type === 'tv' ? `/detail/tv/${item.id}` : `/detail/news/${item.id}`;

  return (
    <div className="mx-auto w-full max-w-site px-4 py-8 md:px-6 md:py-10">
      <header className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
        <h1 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">站内搜索</h1>
        <p className="mt-2 text-sm text-foreground-500">
          输入关键词，搜索站内收录的电视剧剧集与花絮资讯。
        </p>

        <form onSubmit={handleSubmit} role="search" className="mt-5 flex flex-col gap-3 sm:flex-row">
          <div className="flex h-12 flex-1 items-center gap-2 rounded-full border border-background-300 bg-background-50 pl-4 pr-4 focus-within:border-primary-400">
            <i className="ri-search-line text-lg text-foreground-400" />
            <input
              type="search"
              name="q"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="搜索电视剧名称、类型或花絮关键词…"
              aria-label="搜索关键词"
              className="min-w-0 flex-1 bg-transparent text-base text-foreground-900 outline-none placeholder:text-foreground-400"
            />
          </div>
          <button
            type="submit"
            className="inline-flex h-12 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-8 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
          >
            搜索
          </button>
        </form>

        <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
          <span>热门搜索：</span>
          {hotKeywords.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => {
                setInput(item);
                setSearchParams({ q: item });
              }}
              className="rounded-full bg-background-100 px-3 py-1 text-sm transition-colors hover:bg-accent-100 hover:text-accent-900"
            >
              {item}
            </button>
          ))}
        </div>
      </header>

      {keyword && (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-foreground-600">
            关键词 <strong className="text-primary-600">“{keyword}”</strong> 共找到{' '}
            <strong className="text-primary-600">{results.length}</strong> 条结果
          </p>
          <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
            前端演示数据 · 非真实后台检索
          </span>
        </div>
      )}

      <div className="mt-6">
        {!keyword ? (
          <EmptyState
            icon="ri-search-line"
            title="输入关键词开始搜索"
            description="你可以搜索剧名、类型标签或花絮关键词，例如“悬疑”“专访”。"
          />
        ) : results.length > 0 ? (
          <div className="space-y-4">
            {results.map((item) => (
              <Link
                key={item.key}
                to={linkFor(item)}
                className="group flex flex-col gap-4 overflow-hidden rounded-lg border border-background-200 bg-background-50 p-3 transition-all duration-300 hover:border-primary-300 sm:flex-row"
              >
                <div className="relative h-44 w-full shrink-0 overflow-hidden rounded-md sm:h-32 sm:w-48">
                  <img
                    src={item.image}
                    alt={item.title}
                    title={`${item.title} - 搜索结果`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="flex min-w-0 flex-1 flex-col justify-center py-1">
                  <div className="flex items-center gap-2">
                    <span
                      className={`rounded-full px-2.5 py-0.5 text-xs font-label ${
                        item.type === 'tv'
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-accent-500 text-primary-950'
                      }`}
                    >
                      {item.type === 'tv' ? '电视剧' : '花絮'}
                    </span>
                    <span className="text-xs text-foreground-400">{item.meta}</span>
                  </div>
                  <h3 className="mt-2 line-clamp-1 font-heading text-base font-bold text-foreground-950 transition-colors group-hover:text-primary-500">
                    {item.title}
                  </h3>
                  <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-500">
                    {item.excerpt}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="space-y-10">
            <EmptyState
              icon="ri-emotion-sad-line"
              title={`没有找到与“${keyword}”相关的内容`}
              description="试试更换关键词，或浏览下方为你推荐的热门剧集。"
            />
            <section>
              <h2 className="mb-6 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <span className="h-5 w-1 rounded-full bg-accent-500" />
                相关推荐
              </h2>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {recommend.map((drama) => (
                  <DramaCard key={drama.id} drama={drama} />
                ))}
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}